import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Loader2 } from 'lucide-react'

import {
  attachPersonalScript,
  forkPersonalScript,
  listProductions,
  listScriptDocuments,
  previewWriterImport,
  type ScriptDocument,
  type WriterImportPart,
  type WriterImportPartPreview,
} from '@/api/studio-production'
import { AuthSessionExpiredError } from '@/api/authSession'
import { formatResolvedApiError, resolveApiErrorMessage } from '@/features/studio/lib/resolveApiError'
import { ProtoBtn, ProtoLabel, ProtoModal, ProtoSelect, ProtoSegmented } from '@/features/studio/components/ui-kit'
import { Checkbox } from '@/shared/components/ui/checkbox'
import { Label } from '@/shared/components/ui/label'

const WRITER_IMPORT_PARTS: WriterImportPart[] = [
  'series_outline',
  'story_content',
  'seasons',
  'episodes',
  'scenes',
]

const PART_LABEL_KEYS: Record<WriterImportPart, string> = {
  series_outline: 'writer.import.partSeriesOutline',
  story_content: 'writer.import.partStoryContent',
  seasons: 'writer.import.partSeasons',
  episodes: 'writer.import.partEpisodes',
  scenes: 'writer.import.partScenes',
}

function defaultSelectedParts(parts: WriterImportPartPreview[]): Set<WriterImportPart> {
  return new Set(parts.filter((item) => item.source && !item.imported).map((item) => item.part))
}

export function WriterImportToProductionModal({
  open,
  onClose,
  writerScriptId,
  episodeNo,
}: {
  open: boolean
  onClose: () => void
  writerScriptId: number
  episodeNo: number
}) {
  const { t } = useTranslation()
  const [productionId, setProductionId] = useState('')
  const [targetScriptId, setTargetScriptId] = useState('')
  const [productions, setProductions] = useState<{ public_id: string; title: string }[]>([])
  const [scriptDocs, setScriptDocs] = useState<ScriptDocument[]>([])
  const [loading, setLoading] = useState(false)
  const [scriptsLoading, setScriptsLoading] = useState(false)
  const [previewLoading, setPreviewLoading] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [mode, setMode] = useState<'fork' | 'attach'>('fork')
  const [episodeScope, setEpisodeScope] = useState<'current' | 'all'>('current')
  const [selectedParts, setSelectedParts] = useState<Set<WriterImportPart>>(new Set())
  const [partStatus, setPartStatus] = useState<Record<string, WriterImportPartPreview>>({})
  const [episodeCount, setEpisodeCount] = useState(1)
  const [previewTick, setPreviewTick] = useState(0)
  const [error, setError] = useState<string | null>(null)
  const [result, setResult] = useState<string | null>(null)
  const defaultsKeyRef = useRef('')

  useEffect(() => {
    if (!open) return
    setError(null)
    setResult(null)
    setEpisodeScope('current')
    setMode('fork')
    setSelectedParts(new Set())
    setPartStatus({})
    setPreviewTick(0)
    defaultsKeyRef.current = ''
    setLoading(true)
    void listProductions(100, 0)
      .then((res) => {
        setProductions(res.items.map((p) => ({ public_id: p.public_id, title: p.title })))
        if (res.items[0]) setProductionId(res.items[0].public_id)
      })
      .catch(() => setProductions([]))
      .finally(() => setLoading(false))
  }, [open])

  useEffect(() => {
    if (!open || !productionId) {
      setScriptDocs([])
      setTargetScriptId('')
      return
    }
    setScriptsLoading(true)
    void listScriptDocuments(productionId, { limit: 50, offset: 0 })
      .then((res) => {
        const items = res.items ?? []
        setScriptDocs(items)
        setTargetScriptId(items[0] ? String(items[0].id) : '')
      })
      .catch(() => {
        setScriptDocs([])
        setTargetScriptId('')
      })
      .finally(() => setScriptsLoading(false))
  }, [open, productionId])

  useEffect(() => {
    if (!open || !productionId || !targetScriptId) {
      setPartStatus({})
      return
    }
    const scriptIdNum = Math.max(1, Number.parseInt(targetScriptId, 10) || 0)
    if (!scriptIdNum) return
    let cancelled = false
    setPreviewLoading(true)
    void previewWriterImport(productionId, scriptIdNum, {
      writer_script_id: writerScriptId,
      episode_nos: episodeScope === 'current' ? [episodeNo] : [],
    })
      .then((res) => {
        if (cancelled) return
        const nextStatus: Record<string, WriterImportPartPreview> = {}
        for (const item of res.parts ?? []) {
          nextStatus[item.part] = item
        }
        setPartStatus(nextStatus)
        setEpisodeCount(Math.max(1, res.episode_count || 1))
        const defaultsKey = `${productionId}:${scriptIdNum}`
        if (defaultsKeyRef.current !== defaultsKey) {
          defaultsKeyRef.current = defaultsKey
          setSelectedParts(defaultSelectedParts(res.parts ?? []))
        }
      })
      .catch((e) => {
        if (cancelled || e instanceof AuthSessionExpiredError) return
        setError(formatResolvedApiError(t, resolveApiErrorMessage(e, 'writer.import.failed')))
      })
      .finally(() => {
        if (!cancelled) setPreviewLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [open, productionId, targetScriptId, writerScriptId, episodeNo, episodeScope, previewTick, t])

  function togglePart(part: WriterImportPart, enabled: boolean) {
    setSelectedParts((prev) => {
      const next = new Set(prev)
      if (enabled) next.add(part)
      else next.delete(part)
      return next
    })
  }

  async function handleSubmit() {
    if (!productionId) {
      setError(t('writer.import.productionRequired'))
      return
    }
    if (!targetScriptId) {
      setError(t('writer.import.scriptRequired'))
      return
    }
    const scriptIdNum = Math.max(1, Number.parseInt(targetScriptId, 10) || 0)
    if (!scriptIdNum) {
      setError(t('writer.import.scriptRequired'))
      return
    }
    const parts = WRITER_IMPORT_PARTS.filter((part) => selectedParts.has(part))
    if (parts.length === 0) {
      setError(t('writer.import.partsRequired'))
      return
    }
    setSubmitting(true)
    setError(null)
    try {
      const payload = {
        writer_script_id: writerScriptId,
        personal_script_id: writerScriptId,
        episode_nos: episodeScope === 'current' ? [episodeNo] : [],
        overwrite_draft_only: true,
        parts,
      }
      const res =
        mode === 'attach'
          ? await attachPersonalScript(productionId, scriptIdNum, payload)
          : await forkPersonalScript(productionId, scriptIdNum, payload)
      setResult(
        t(mode === 'attach' ? 'writer.import.attachSuccess' : 'writer.import.success', {
          imported: res.imported_episode_nos.join(', ') || '—',
          skipped: res.skipped_episode_nos.join(', ') || '—',
        }),
      )
      defaultsKeyRef.current = ''
      setPreviewTick((n) => n + 1)
    } catch (e) {
      if (e instanceof AuthSessionExpiredError) return
      setError(formatResolvedApiError(t, resolveApiErrorMessage(e, 'writer.import.failed')))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <ProtoModal
      open={open}
      onClose={onClose}
      title={t('writer.import.title')}
      footer={
        <>
          <ProtoBtn size="sm" variant="secondary" onClick={onClose} disabled={submitting}>
            {t('common.close')}
          </ProtoBtn>
          <ProtoBtn size="sm" disabled={submitting || loading || scriptsLoading || !targetScriptId} onClick={() => void handleSubmit()}>
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            {t('writer.import.submit')}
          </ProtoBtn>
        </>
      }
    >
      <p className="mb-4 text-xs text-zinc-500">
        {t(mode === 'attach' ? 'writer.import.attachSubtitle' : 'writer.import.subtitle', {
          n: mode === 'attach' && episodeScope === 'current' ? episodeNo : episodeCount,
        })}
      </p>
      <div className="mb-3">
        <ProtoSegmented
          options={[
            { value: 'fork', label: t('writer.import.modeFork') },
            { value: 'attach', label: t('writer.import.modeAttach') },
          ]}
          value={mode}
          onChange={(v) => setMode(v as 'fork' | 'attach')}
        />
      </div>
      <div className="mb-4">
        <ProtoSegmented
          options={[
            { value: 'current', label: t('writer.import.scopeCurrent') },
            { value: 'all', label: t('writer.import.scopeAll') },
          ]}
          value={episodeScope}
          onChange={(v) => setEpisodeScope(v as 'current' | 'all')}
        />
      </div>
      {error ? <p className="mb-3 text-sm text-rose-300">{error}</p> : null}
      {result ? <p className="mb-3 text-sm text-emerald-300">{result}</p> : null}
      {loading || scriptsLoading ? (
        <Loader2 className="mx-auto h-6 w-6 animate-spin text-indigo-400" />
      ) : productions.length === 0 ? (
        <p className="text-sm text-zinc-500">{t('writer.import.noProductions')}</p>
      ) : (
        <div className="space-y-3">
          <div>
            <ProtoLabel>{t('writer.import.production')}</ProtoLabel>
            <ProtoSelect value={productionId} onChange={(e) => setProductionId(e.target.value)}>
              {productions.map((p) => (
                <option key={p.public_id} value={p.public_id}>
                  {p.title}
                </option>
              ))}
            </ProtoSelect>
          </div>
          <div>
            <ProtoLabel>{t('writer.import.targetScript')}</ProtoLabel>
            {scriptDocs.length === 0 ? (
              <p className="mt-1 text-xs text-amber-400/90">{t('writer.import.noScripts')}</p>
            ) : (
              <ProtoSelect value={targetScriptId} onChange={(e) => setTargetScriptId(e.target.value)}>
                {scriptDocs.map((doc, index) => (
                  <option key={doc.id} value={String(doc.id)}>
                    {doc.title?.trim() || t('writer.import.scriptN', { n: index + 1 })}
                  </option>
                ))}
              </ProtoSelect>
            )}
          </div>
          <div>
            <div className="mb-2 flex items-center justify-between gap-2">
              <ProtoLabel>{t('writer.import.partsTitle')}</ProtoLabel>
              {previewLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin text-zinc-500" /> : null}
            </div>
            <div className="space-y-2 rounded-xl border border-white/8 bg-black/20 px-3 py-2">
              {WRITER_IMPORT_PARTS.map((part) => {
                const status = partStatus[part]
                const imported = status?.imported === true
                const disabled = status != null && !status.source
                return (
                  <div key={part} className="flex items-center gap-2">
                    <Checkbox
                      id={`writer-import-part-${part}`}
                      checked={selectedParts.has(part)}
                      disabled={disabled}
                      onCheckedChange={(checked) => togglePart(part, checked === true)}
                    />
                    <Label
                      htmlFor={`writer-import-part-${part}`}
                      className={`text-xs font-normal ${disabled ? 'text-zinc-600' : 'text-zinc-300'}`}
                    >
                      {t(PART_LABEL_KEYS[part])}
                    </Label>
                    {imported ? (
                      <span className="rounded-full bg-emerald-500/15 px-1.5 py-0.5 text-[10px] text-emerald-300">
                        {t('writer.import.partImported')}
                      </span>
                    ) : null}
                    {disabled ? (
                      <span className="text-[10px] text-zinc-600">{t('writer.import.partNoSource')}</span>
                    ) : null}
                  </div>
                )
              })}
            </div>
            <p className="mt-2 text-[11px] text-zinc-600">{t('writer.import.partsHint')}</p>
          </div>
        </div>
      )}
    </ProtoModal>
  )
}
